import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../component/confirmation-dialog/confirmation-dialog.component';

// Componentes com forms editáveis implementam esta interface para que o
// UnsavedChangesGuard saiba quando interceptar a navegação. Interface
// genérica (não acoplada ao FormGroup) permite reuso em qualquer página
// com mudanças não-persistidas, inclusive futuras que não usem Reactive Forms.
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

/** Componentes que precisam de um diálogo CUSTOMIZADO na saída (ex.: 3-way
 *  save/discard/cancel do Dashboard) implementam este método. Quando presente,
 *  o guard o delega em vez de abrir o diálogo genérico discard-only. */
export interface HasCustomCanDeactivate {
  canDeactivate(): Observable<boolean> | boolean;
}

@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<HasUnsavedChanges | HasCustomCanDeactivate> {
  constructor(private dialog: MatDialog) { }

  canDeactivate(component: HasUnsavedChanges | HasCustomCanDeactivate): Observable<boolean> | boolean {
    if (!component) { return true; }

    // Delegação pro componente quando ele expõe seu próprio fluxo de saída
    // (permite diálogos 3-way tipo save/discard/cancel, específicos por página).
    const custom = (component as HasCustomCanDeactivate).canDeactivate;
    if (typeof custom === 'function') {
      return custom.call(component);
    }

    if (!(component as HasUnsavedChanges).hasUnsavedChanges()) {
      return true;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'unsaved-changes-dialog.title',
        content: 'unsaved-changes-dialog.content',
        cancel: 'forms.buttons.cancel',
        confirm: 'forms.buttons.discard-and-leave',
        destructive: true
      }
    });

    return dialogRef.afterClosed().pipe(map(result => result === true));
  }
}
